import { isCompletedReport } from '@/utils';
import type { ReportData } from '@/components/preview/types';

/**
 * The data-only answer to "is this report in a state the API will accept?".
 *
 * Takes the report instead of reading it from a closure so a caller that just
 * mutated `formDataRef` can ask about the value it wrote, not the one the last
 * render saw.
 */
export const canSaveReportData = (data: ReportData) =>
    !isCompletedReport(data) && !!data.patient?.id;

interface ReportCreationConfirmationState {
    report: ReportData;
    /** The `?id=` query param — set for a report that already exists server-side. */
    reportId: string | null;
    isLoading: boolean;
    /** The patient whose "create the report?" question the user already answered. */
    answeredForPatientId: string | null;
}

/**
 * A patient landed on a report that does not exist yet, and the user has not
 * yet answered whether to create it for them.
 *
 * Derived on every render and never stored, so everything that has to agree
 * about this state — the confirmation modal, the autosave, the template prompt
 * and the blocked step panel — reads the same truth within the same commit
 * instead of racing each other's effects.
 */
export const needsReportCreationConfirmation = ({
    report,
    reportId,
    isLoading,
    answeredForPatientId,
}: ReportCreationConfirmationState) => {
    if (isLoading || reportId || report.id || isCompletedReport(report)) return false;

    const patientId = report.patient?.id;

    return Boolean(patientId) && patientId !== answeredForPatientId;
};
